import { useMemo, useState } from 'react'
import { useParams} from 'react-router-dom'
import { makeCoreGetFileRequest, makeCoreGetRequest } from "utils/api"
import { Table } from 'components/core-ui/Table'
import { useNotification } from 'hooks'
import { formatErrorMessage } from 'utils/formatting'
import { toTableRowItems } from './serializers'
import { useQuery } from 'react-query'
import { formatAndDownloadResponse } from 'utils/helpers'
import { columns, drawerFilters, toolbarActions } from './helpers'
import s from "../../../../../commonStyles.modules.scss"

type FetchArgs = {
  page: number
  rowsPerPage: number
  order?: 'asc' | 'desc'
  orderBy?: string
  tableFilters?: Array<{ name: string; value: any }>
}

const ViewDetails = ({ isHistorical }) => {
  const { showNotification } = useNotification()
  const { id } = useParams()

  const [rawLoaded, setRawLoaded] = useState(false)
  const [allRows, setAllRows] = useState<any[]>([])
  const [tableItems, setTableItems] = useState<any[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [isExcelExportLoading, setIsExcelExportLoading] = useState<boolean>(false)

  const applyFilters = (rows: any[], tableFilters?: FetchArgs['tableFilters']) => {
    if (!tableFilters?.length) return rows

    let filtered = rows
    for (const f of tableFilters) {
      const v = f?.value
      if (v === undefined || v === null || v === '') continue

      const val = String(v).toLowerCase()

      switch (f.name) {
        case 'eanCode':
        case 'articleCode':
        case 'scannedCode':
        case 'measureUnit': {
          filtered = filtered.filter(r =>
            String(r[f.name] ?? '').toLowerCase().includes(val)
          )
          break
        }
        default:
          break
      }
    }
    return filtered
  }

  const applySorting = (
    rows: any[],
    order?: 'asc' | 'desc',
    orderBy?: string
  ) => {
    if (!orderBy || !order || rows.length <= 1) return rows

    const dir = order === 'desc' ? -1 : 1

    const getComparable = (row: any) => {
      switch (orderBy) {
        case 'receivedQuantity':
          return row.receivedQuantityValue
        case 'orderedQuantity':
          return row.orderedQuantityValue
        case 'difference':
          return row.differenceRaw
        case 'orderLineNumber':
          return row.orderLineNumberRaw ?? (isNaN(Number(row.orderLineNumber)) ? null : Number(row.orderLineNumber))
        case 'expirationDate': {
          const raw = row.expirationDateRaw ?? row.expirationDate
          if (!raw) return null
          const t = Date.parse(raw)
          return isNaN(t) ? null : t
        }
        default: {
          const v = row[orderBy]
          if (v === null || v === undefined) return ''
          return String(v).toLowerCase()
        }
      }
    }

    const safeCompare = (a: any, b: any) => {
      const av = getComparable(a)
      const bv = getComparable(b)

      if (av === null || av === '') return (bv === null || bv === '') ? 0 : 1
      if (bv === null || bv === '') return -1

      if (typeof av === 'number' && typeof bv === 'number') {
        return av === bv ? 0 : av < bv ? -1 : 1
      }

      const an = Number(av), bn = Number(bv)
      if (!Number.isNaN(an) && !Number.isNaN(bn)) {
        return an === bn ? 0 : an < bn ? -1 : 1
      }

      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' })
    }

    return [...rows].sort((a, b) => safeCompare(a, b) * dir)
  }

  const applyPagination = (rows: any[], page: number, rowsPerPage: number) => {
    const start = page * rowsPerPage
    return rows.slice(start, start + rowsPerPage)
  }

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters }) => {
    setIsLoading(true)

    try {
      let rows = allRows

      if (!rawLoaded) {
        const baseUrl = isHistorical
          ? `/shop-reception/ds-orders-archive/details/${id}`
          : `/shop-reception/ds-orders/details/${id}`

        const response = await makeCoreGetRequest(baseUrl)
        const list = response?.dsReceptionProductDTOList ?? []
        const full = toTableRowItems(list)
        const totalCount = full?.length

        rows = full
        setAllRows(full)
        setRawLoaded(true)
        setTotalCount(totalCount)
      }

      const filtered = applyFilters(rows, tableFilters)
      const sorted = applySorting(filtered, order, orderBy)
      const pageSlice = applyPagination(sorted, page, rowsPerPage)

      setTableItems(pageSlice)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la obtinerea datelor din tabel. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const { refetch: exportFileExcel } = useQuery(
    ['exportFiles'],
    async () => {
      setIsExcelExportLoading(true)
      const response = await makeCoreGetFileRequest(
        (isHistorical)
          ? `/shop-reception/ds-orders-archive/details/${id}/export`
          : `/shop-reception/ds-orders/details/${id}/export`,
        {
          responseType: 'blob'
        }
      )
      setIsExcelExportLoading(false)
      return formatAndDownloadResponse(response)
    },
    {
      onError: error => {
        setIsExcelExportLoading(false)
        showNotification({
          message: `A aparut o eroare la descarcarea fisierului. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      },
      enabled: false
    }
  )

  const toolbar = useMemo(() => toolbarActions({
      totalCount,
      exportFileExcel,
      isExcelExportLoading
  }), [totalCount, isExcelExportLoading])

  return (
    <div className={s.mainContainer}>
      <Table
          hideCheckbox
          columns={columns}
          items={tableItems}
          fetchData={fetchData}
          isLoading={isLoading}
          totalCount={totalCount}
          shouldRefreshData={shouldRefreshData}
          setShouldRefreshData={setShouldRefreshData}
          filters={drawerFilters()}
          disableSorting={false}
          disablePagination={false}
          toolbarActions={toolbar}
          filtersLabel="Filtreaza"
          filtersButtonLabel="Filtreaza"
          appliedFiltersLabel="Filtre"
          cancelFiltersButtonLabel="Anuleaza"
          applyFiltersButtonLabel="Aplica"
          rowActionsWidth="130px"
          rowsPerPage={10}
          rowsPerPageOptions={[10, 25, 50]}
      />
    </div>
  )
}

export default ViewDetails
